/* eslint-disable */
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { Magazine } from 'interfaces/Taxonomy';
import MagazineList from 'components/MagazineList';
import MagazineListItem from 'components/MagazineList/MagazineListItem';
import { RightRailSimple } from 'components/Ad/AdSlot';
import type { PageInfo } from 'interfaces/components/PageInfo';
import MostPopularRR from 'components/MostPopularRR';
import type { ArticlePreview } from 'interfaces/content/articles/Articles';
import { layoutCount } from 'constants/constants';
import ObservableWrapper from 'components/ObservableWrapper/ObservableWrapper';
import Button from 'components/Globals/Base/Button';
import { trackEvent } from 'services/Gtm/functions';
import SpinningLoader from 'components/Loader/SpinningLoader';
import { RightRailStickyAd } from 'components/Ad/AdSlot/Styles';
import * as S from './MagazineStyles';
import ErrorBoundary from 'components/ErrorBoundary';
import { UIFragments } from 'utils/log/constants/uiFragments';
import { AppErrorSeverity } from 'utils/log/constants/errorSeverity';
import AdSlotToggle from 'components/Ad/AdSlot/AdSlotToggle';

export interface MagazinesProps {
  magazines: Magazine[];
  magazinePageInfo: PageInfo;
  mostPopular: ArticlePreview[];
  pageNum: number;
  pageSize: number;
}

interface MagazineResponse {
  magazinePageInfo: PageInfo;
  magazines: Magazine[];
}

const MagazinePage: React.FC<MagazinesProps> = ({ magazines, magazinePageInfo, mostPopular, pageNum, pageSize }) => {
  const numOfPosts = layoutCount.magazine;
  const { totalCount } = magazinePageInfo;

  const [magazinePosts, setMagazinePosts] = useState(magazines);
  const [currPageNum, setCurrPageNum] = useState(pageNum);
  const [showLoader, setShowLoader] = useState(false);
  const [nextFocus, setNextFocus] = useState<number | undefined>();
  const [shouldLoadMore, setShouldLoadMore] = useState(totalCount > numOfPosts);

  const fetchMoreMagazines = useCallback(async () => {
    if (!shouldLoadMore) return;
    setShouldLoadMore(false);
    setShowLoader(true);
    const response = await fetch(`/api/getMagazines/${currPageNum + 1}/`);
    const magazinesResponse = (await response.json()) as MagazineResponse;
    setMagazinePosts((prev) => [...prev, ...magazinesResponse.magazines]);
    setCurrPageNum((prev) => prev + 1);
    setShowLoader(false);
    trackEvent({
      eventAction: `content load ${currPageNum}`,
      eventCategory: 'navigation',
      eventLabel: 'scroll',
    });
  }, [currPageNum, shouldLoadMore]);

  useEffect(() => {
    setShouldLoadMore(totalCount > currPageNum * (numOfPosts + 1));
  }, [currPageNum, numOfPosts, totalCount]);

  return (
    <S.MagazinesWrapper>
      <ErrorBoundary fragment={UIFragments.MAGAZINE_PAGE}>
        <S.Header
          as='h1'
          $fontFamily='graphikCond'
          $size={{ default: 'large', mdMin: 'xlarge' }}
        >
          Magazine
        </S.Header>
      </ErrorBoundary>
      <S.MagazinesGridLayout>
        <S.MagazineContent>
          <ErrorBoundary
            fragment={UIFragments.MAGAZINE_PAGE}
            severity={AppErrorSeverity.HIGH}
          >
            <MagazineList pageSize={pageSize}>
              {magazinePosts.map((magazine, index) => (
                <ObservableWrapper
                  key={magazine.title}
                  // eslint-disable-next-line no-void
                  isVisible={() => {
                    void fetchMoreMagazines();
                  }}
                  isLast={index + 1 === magazinePosts?.length && 0 !== currPageNum % 3}
                  focus={index === nextFocus}
                >
                  <MagazineListItem
                    databaseId={magazine.databaseId}
                    id={magazine.id}
                    image={magazine.image}
                    key={magazine.id}
                    link={magazine.link}
                    title={magazine.title}
                    uri={magazine.uri}
                  />
                </ObservableWrapper>
              ))}
            </MagazineList>
          </ErrorBoundary>
          <ErrorBoundary fragment={UIFragments.MAGAZINE_PAGE}>
            {
              // eslint-disable-next-line no-void
              shouldLoadMore && 0 === currPageNum % 3 && (
                <>
                  <Button
                    className='load-more'
                    onClick={() => {
                      setNextFocus(currPageNum * (numOfPosts + 1));
                      void fetchMoreMagazines();
                    }}
                    variant='secondary'
                    size={{ default: 'large' }}
                  >
                    Load More
                  </Button>
                </>
              )
            }
            <SpinningLoader
              className='spinning-loader'
              show={showLoader}
            />
          </ErrorBoundary>
        </S.MagazineContent>
        <ErrorBoundary fragment={UIFragments.MAGAZINE_PAGE}>
          <S.MagazineSideBar>
            <RightRailSimple id={0} />
            {mostPopular && <MostPopularRR articles={mostPopular} />}
            <RightRailStickyAd>
              <AdSlotToggle
                placementName='RightRailFlex'
                sticky
                index={3}
                slotId={`RightRailFlex${3}`}
              />
            </RightRailStickyAd>
          </S.MagazineSideBar>
        </ErrorBoundary>
      </S.MagazinesGridLayout>
    </S.MagazinesWrapper>
  );
};

export { MagazinePage };
